import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  Collapse,
  List,
  ListItem,
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Typography,
} from '@material-tailwind/react';
import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import useUserHook from '../../../hooks/useUserHook';
import { useCommunityLinks } from '../../../hooks/useCommunityLinks';
import { useCourseFinderLinks } from '../../../hooks/useCourseFinderLinks';

interface SubMenuProps {
  title: string;
  url: string;
  blank?: boolean;
}

interface NavListProps {
  text: string;
  obj: SubMenuProps[];
}

/** 하위 메뉴 드롭다운 (lg 이상: Menu 드롭다운, lg 미만: Collapse 펼침) */
function NavListMenu({ text, obj }: NavListProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const renderItems = obj.map(({ title, url, blank }: SubMenuProps, key) => {
    const item = (
      <MenuItem className="flex items-center gap-3 rounded-lg">
        <div>
          <Typography
            variant="h6"
            color="blue-gray"
            className="flex items-center text-sm font-medium"
          >
            {title}
          </Typography>
        </div>
      </MenuItem>
    );

    // 내부 경로는 새로고침 없이 이동 (앱 재로딩·API 재호출 방지)
    return blank || !url.startsWith('/') ? (
      <a href={url} key={key} target={blank ? '_blank' : undefined}>
        {item}
      </a>
    ) : (
      <Link to={url} key={key}>
        {item}
      </Link>
    );
  });

  return (
    <Fragment>
      <Menu
        open={isMenuOpen}
        handler={setIsMenuOpen}
        offset={{ mainAxis: 20 }}
        placement="bottom"
      >
        <MenuHandler>
          <Typography as="div" variant="small" className="font-medium">
            <ListItem
              className="flex items-center gap-2 py-2 pr-4 font-medium text-gray-900"
              selected={false}
              onClick={() => setIsMobileMenuOpen((cur) => !cur)}
            >
              {text}
              <ChevronDownIcon
                strokeWidth={2.5}
                className={`hidden h-3 w-3 transition-transform lg:block ${
                  isMenuOpen ? 'rotate-180' : ''
                }`}
              />
              <ChevronDownIcon
                strokeWidth={2.5}
                className={`block h-3 w-3 transition-transform lg:hidden ${
                  isMobileMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </ListItem>
          </Typography>
        </MenuHandler>
        <MenuList className="hidden max-w-screen-xl rounded-xl lg:block">
          <ul
            className={`grid grid-cols-[${obj.length}] gap-y-2 outline-none outline-0`}
          >
            {renderItems}
          </ul>
        </MenuList>
      </Menu>
      <div className="block lg:hidden">
        <Collapse open={isMobileMenuOpen}>{renderItems}</Collapse>
      </div>
    </Fragment>
  );
}

/** 교사(일반 회원)용 헤더 메뉴 목록 */
export const NavList = () => {
  const { checkAndPageMove } = useUserHook();

  // 코스 찾기 메뉴 목록
  const { links: courseLinks } = useCourseFinderLinks();
  const courseSubMenu: SubMenuProps[] = courseLinks.map(
    ({ name, linkUrl }) => ({
      title: name,
      url: linkUrl,
      blank: false,
    }),
  );

  // 커뮤니티 메뉴 목록
  const { links: communityLinks } = useCommunityLinks();
  const communitySubMenu: SubMenuProps[] = communityLinks.map(
    ({ name, linkUrl }) => ({
      title: name,
      url: linkUrl,
      blank: true,
    }),
  );

  return (
    <List className="mb-2 mt-2 flex flex-col gap-2 lg:mb-0 lg:mt-0 lg:flex-row lg:items-center">
      <NavListMenu text="코스 찾기" obj={courseSubMenu} />

      {/* 내 학습 (교사대학) - 로그인 체크 */}
      <Typography
        as="a"
        variant="small"
        color="blue-gray"
        className="font-medium"
        onClick={() => {
          checkAndPageMove('/learning');
        }}
      >
        <ListItem className="flex items-center gap-2 py-2 pr-4">
          내 학습 (교사대학)
        </ListItem>
      </Typography>

      {/* 클래스 관리 - 로그인 체크 */}
      <Typography
        as="a"
        variant="small"
        color="blue-gray"
        className="font-medium"
        onClick={() => {
          checkAndPageMove('/ClassManagement');
        }}
      >
        <ListItem className="flex items-center gap-2 py-2 pr-4">
          클래스 관리
        </ListItem>
      </Typography>

      <NavListMenu text="커뮤니티" obj={communitySubMenu} />

      {/* 회원 후기 */}
      <Link to="/review">
        <Typography
          as="div"
          variant="small"
          color="blue-gray"
          className="font-medium"
        >
          <ListItem className="flex items-center gap-2 py-2 pr-4">
            회원 후기
          </ListItem>
        </Typography>
      </Link>

      {/* SHOP */}
      <Link to="/shop">
        <Typography
          as="div"
          variant="small"
          color="blue-gray"
          className="font-medium"
        >
          <ListItem className="flex items-center gap-2 py-2 pr-4">
            SHOP
          </ListItem>
        </Typography>
      </Link>
    </List>
  );
};
